import type { AudioForm } from '@/features/audio-form/domain/audio-form.ts'
import type { Clip } from '@/features/clips/domain/models/clip.ts'
import { create } from 'zustand'
import useMediaStore from './media.store'

export const MIX_ID = 'mix'
export const VOICE_TRACK_ID = 'voice-track'
export const SOUND_DESIGN_TRACK_ID = 'design-track'
export const MEDIA_FILES_TRACK_ID = 'media-track'
export const SOUND_EFFECTS_TRACK_ID = 'sound-effects-track'

import { DEFAULT_TRACK_GAIN_DB } from '../track-gain.ts'

export const DEFAULT_MAX_PROJECT_DURATION = 120
export {
  DEFAULT_TRACK_GAIN_DB,
  MAX_TRACK_GAIN_DB,
  MIN_TRACK_GAIN_DB,
} from '../track-gain.ts'

export enum TrackType {
  Voices = 'voices',
  SoundDesigns = 'soundDesigns',
  MediaFiles = 'mediaFiles',
  SoundEffects = 'soundEffects',
}

export type Track = {
  id: string
  type: TrackType
  clips: Clip[]
  volume: number
  isMuted: boolean
  isSolo: boolean
  isForceMuted: boolean
  smartFit?: boolean
}

export interface TracksData {
  tracks: Track[]
  divisionsPerSecond: number
  endTime: number
  startTime: number
  playbackTime: number
}

export interface TracksStore extends TracksData {
  addClip: (trackId: string, clip: Clip) => void
  updateClip: (id: string, clip: Partial<Clip>) => void
  deleteClip: (trackId: string, id: string) => void
  setDivisionsPerSecond: (divisionsPerSecond: number) => void
  solo: (trackId: string) => void
  mute: (trackId: string) => void
  getTrackState: (id: string) => Track
  setVolume: (trackId: string, volume: number) => void
  updateTrack: (trackId: string, values: Partial<Track>) => void
  setPlaybackTime: (timer: number) => void
  updateStartAndEndDates: () => void
  getSnapshot: () => TracksData
  setSnapshot: (data: TracksData) => void
  addMediaFileTrack: () => string
  deleteTrack: (trackId: string) => void
  draggedClip: Clip | undefined
  setDraggedClip: (clip: Clip | undefined) => void
  getAvailablePosition: (
    clip: Clip,
    magnetDistance: number,
    trackId: string
  ) => number | undefined
  /**
   * Returns the minimum start offset that the clip can have taking into account the other clips
   */
  getMinStartOffset: (clip: Clip) => number
  /**
   * Returns the minimum end offset that the clip can have taking into account the other clips
   */
  getMinEndOffset: (clip: Clip) => number
  generatedMix: AudioForm | undefined
  setGeneratedMix: (mix?: AudioForm) => void
  isMixEmpty: boolean
  maxProjectDuration: number
}

export const trackSelector =
  (id: string) =>
  (state: TracksStore): Track =>
    state.getTrackState(id)

export const clipSelector =
  (trackId: string, clipId: string) =>
  (state: TracksStore): Clip | undefined => {
    return state.getTrackState(trackId).clips.find((clip) => clip.id === clipId)
  }

export const TracksStoreInitialState: TracksData = {
  tracks: [
    {
      id: VOICE_TRACK_ID,
      type: TrackType.Voices,
      clips: [],
      volume: DEFAULT_TRACK_GAIN_DB,
      isMuted: false,
      isSolo: false,
      isForceMuted: false,
    },
    {
      id: SOUND_DESIGN_TRACK_ID,
      type: TrackType.SoundDesigns,
      clips: [],
      volume: DEFAULT_TRACK_GAIN_DB,
      isMuted: false,
      isSolo: false,
      isForceMuted: false,
      smartFit: false,
    },
    {
      id: SOUND_EFFECTS_TRACK_ID,
      type: TrackType.SoundEffects,
      clips: [],
      volume: DEFAULT_TRACK_GAIN_DB,
      isMuted: false,
      isSolo: false,
      isForceMuted: false,
    },
    {
      id: MEDIA_FILES_TRACK_ID,
      type: TrackType.MediaFiles,
      clips: [],
      volume: DEFAULT_TRACK_GAIN_DB,
      isMuted: false,
      isSolo: false,
      isForceMuted: false,
    },
  ],
  divisionsPerSecond: 2,
  playbackTime: 0,
  startTime: 0,
  endTime: 0,
}

const useTracksStore = create<TracksStore>((set, get) => ({
  ...TracksStoreInitialState,
  updateStartAndEndDates: () => {
    const clips = get()
      .tracks.flatMap((t) => t.clips)
      .filter((c) => c.startAt !== undefined)

    const endTime = clips.length
      ? Math.max(...clips.map((c) => c.startAt! + c.duration + c.endOffset))
      : 0

    const startTime = clips.length
      ? Math.min(...clips.map((c) => c.startAt! + c.startOffset))
      : 0

    const maxProjectDuration =
      endTime > get().maxProjectDuration
        ? Math.ceil(endTime + 1)
        : get().maxProjectDuration

    set({ endTime, startTime, maxProjectDuration })
  },

  updateClip: (clipId: string, clip: Partial<Clip>) => {
    const state = get()
    const track = state.tracks.find((t) => t.clips.find((c) => c.id === clipId))

    if (!track) return
    state.updateTrack(track.id, {
      clips: track.clips.map((c) =>
        c.id === clipId
          ? {
              ...c,
              ...clip,
            }
          : c
      ),
    })
    get().updateStartAndEndDates()
  },
  setDivisionsPerSecond: (divisionsPerSecond) => set({ divisionsPerSecond }),
  setPlaybackTime: (playbackTime: number) => set({ playbackTime }),
  solo: (trackId: string) => {
    const state = get()
    state.updateTrack(trackId, {
      isSolo: !state.getTrackState(trackId).isSolo,
    })

    const anySoloed = get().tracks.some((track) => track.isSolo)

    if (anySoloed) {
      // If any track is soloed, all other tracks should be force muted
      set((state) => ({
        tracks: state.tracks.map((track) =>
          track.id !== trackId && !track.isSolo
            ? { ...track, ...{ isForceMuted: true } }
            : { ...track, ...{ isForceMuted: false } }
        ),
      }))
    } else {
      // If no track is soloed, no force mute should be applied
      set((state) => ({
        tracks: state.tracks.map((track) => ({
          ...track,
          ...{ isForceMuted: false },
        })),
      }))
    }
  },

  mute: (trackId: string) => {
    const state = get()
    const newMuteState = !state.getTrackState(trackId).isMuted
    const isAnyOtherSoloed = state.tracks.some(
      (track) => track.isSolo && track.id !== trackId
    )

    state.updateTrack(trackId, {
      isMuted: newMuteState,
      isForceMuted: isAnyOtherSoloed && !newMuteState,
    })
  },

  getTrackState: (id: string) => {
    const track = get().tracks.find((track) => track.id === id)
    if (!track) {
      throw new Error('Track not found')
    }
    return track
  },

  setVolume: (trackId: string, volume: number) => {
    get().updateTrack(trackId, { volume })
  },

  addClip: (trackId: string, clip: Clip) => {
    try {
      const currentClips = get().getTrackState(trackId).clips

      get().updateTrack(trackId, { clips: [...currentClips, clip] })
      get().updateStartAndEndDates()
      if (currentClips.length === 0) set({ isMixEmpty: false })
    } catch {
      // This might happen if the clip is dropped outside the track
    }
  },

  deleteClip: (trackId: string, clipId: string) => {
    const currentClips = get().getTrackState(trackId).clips
    get().updateTrack(trackId, {
      clips: currentClips.filter((clip) => clip.id !== clipId),
    })
    get().updateStartAndEndDates()

    for (const track of get().tracks) {
      if (track.clips.length > 0) {
        return
      }
    }
    set({ isMixEmpty: true })
  },

  updateTrack: (id: string, values: Partial<Track>) => {
    set((state) => ({
      tracks: state.tracks.map((track) =>
        track.id === id ? { ...track, ...values } : track
      ),
    }))
  },

  getSnapshot: () => {
    return {
      tracks: get().tracks,
      divisionsPerSecond: get().divisionsPerSecond,
      endTime: get().endTime,
      startTime: get().startTime,
      playbackTime: get().playbackTime,
    }
  },

  setSnapshot: (data: TracksData) => {
    set(data)
  },

  addMediaFileTrack: () => {
    const trackId = crypto.randomUUID()
    set((state) => ({
      tracks: [
        ...state.tracks,
        {
          id: trackId,
          type: TrackType.MediaFiles,
          clips: [],
          volume: DEFAULT_TRACK_GAIN_DB,
          isMuted: false,
          isSolo: false,
          isForceMuted: false, // todo: this should be true if any other track is soloed
        },
      ],
    }))
    return trackId
  },

  deleteTrack: (trackId: string) => {
    set((state) => ({
      tracks: state.tracks.filter((track) => track.id !== trackId),
    }))
  },

  getAvailablePosition: (
    clip: Clip,
    magnetDistance: number,
    trackId: string
  ): number | undefined => {
    const track = get().getTrackState(trackId)

    if (clip.startAt === undefined) return
    let calculatedPosition = clip.startAt

    for (const c of track.clips) {
      if (c.id === clip.id || c.startAt === undefined) {
        continue
      }

      const clipEnding = clip.startAt + clip.duration + clip.endOffset
      const cStart = c.startAt + c.startOffset
      if (Math.abs(clipEnding - cStart) < magnetDistance) {
        calculatedPosition = cStart - clip.duration - clip.endOffset
        if (calculatedPosition - clip.startOffset < 0) continue
        break
      }

      const clipStart = clip.startAt + clip.startOffset
      const cEnding = c.startAt + c.duration + c.endOffset
      if (Math.abs(clipStart - cEnding) < magnetDistance) {
        calculatedPosition = cEnding - clip.startOffset
        if (calculatedPosition - clip.startOffset < 0) continue
        break
      }
    }

    const isOverlapping = track.clips.some((c) => {
      if (c.id === clip.id || c.startAt === undefined) return false

      const clipStart = calculatedPosition + clip.startOffset
      const clipEnding = calculatedPosition + clip.duration + clip.endOffset
      const cStart = c.startAt + c.startOffset
      const cEnding = c.startAt + c.duration + c.endOffset

      // this minDistance is a measure to avoid floating point errors
      const minDistance = magnetDistance / 100

      const startOverlap =
        clipStart < cEnding && Math.abs(clipStart - cEnding) >= minDistance
      const endOverlap =
        clipEnding > cStart && Math.abs(clipEnding - cStart) >= minDistance

      return startOverlap && endOverlap
    })

    if (isOverlapping) return undefined

    return calculatedPosition
  },

  getMinStartOffset: (clip: Clip): number => {
    const track = get().getTrackState(clip.trackId)

    if (clip.startAt === undefined) {
      throw new Error('Malformed clip')
    }

    if (!clip.startOffset) return clip.startOffset

    const clipEnding = clip.startAt + clip.duration + clip.endOffset
    let closestClipEnding = 0

    for (const c of track.clips) {
      if (c.id === clip.id || c.startAt === undefined) {
        continue
      }

      // we need to find the closest clip ending to the current clip
      const cEnding = c.startAt + c.duration + c.endOffset
      if (cEnding < clipEnding) {
        closestClipEnding = Math.max(closestClipEnding, cEnding)
      }
    }

    return closestClipEnding - clip.startAt
  },

  getMinEndOffset: (clip: Clip): number => {
    const track = get().getTrackState(clip.trackId)

    if (clip.startAt === undefined) {
      throw 'Malformed clip'
    }

    if (!clip.endOffset) return clip.endOffset

    const clipStarting = clip.startAt + clip.startOffset
    let closestClipStarting = Number.POSITIVE_INFINITY

    for (const c of track.clips) {
      if (c.id === clip.id || c.startAt === undefined) {
        continue
      }

      // we need to find the closest clip starting to the current clip
      const cStarting = c.startAt + c.startOffset
      if (cStarting > clipStarting) {
        closestClipStarting = Math.min(closestClipStarting, cStarting)
      }
    }
    return closestClipStarting - (clip.startAt + clip.duration)
  },

  draggedClip: undefined,
  setDraggedClip: (clip: Clip | undefined) =>
    set(() => ({ draggedClip: clip })),

  generatedMix: undefined,
  setGeneratedMix: (generatedMix?: AudioForm) => set(() => ({ generatedMix })),

  isMixEmpty: true,
  maxProjectDuration: DEFAULT_MAX_PROJECT_DURATION,
}))

export const useMasteringAvailability = () => {
  const tracks = useTracksStore(({ tracks }) => tracks)
  const { media } = useMediaStore()
  for (const { clips } of tracks) {
    for (const { mediaId } of clips) {
      if (!media[mediaId]) return false
    }
  }
  return true
}

export default useTracksStore
